import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectFeatureFlagPosCart } from '../../redux/features/config/config.selector';
import { selectUserProfile } from '../../selectors/user';
import {
  getLayoutPreference,
  getLeftStackHeight,
  getRightStackHeight,
  removeLayoutPreference,
  removeStackHeights,
  resolvePaneUserKey,
  saveLayoutPreference,
  saveLeftStackHeight,
  saveRightStackHeight,
} from '../../utils/local-storage';
import LayoutPreferenceDialog, {
  DEFAULT_PANE_SIZES,
  LEFT_STACK_DEFAULT_SIZES,
  LayoutPreferenceValues,
  RIGHT_STACK_DEFAULT_SIZES,
} from './LayoutPreferenceDialog';

/**
 * Hosts the "Layout preference" dialog for the whole app (App subtree), so the
 * header ProfileMenu can open it from anywhere — including the task-router lobby,
 * where the 3-pane console (and its PanelGroups) isn't mounted yet.
 *
 * Apply/Reset always persist the agent's explicit choice to local storage; when
 * the console IS mounted, MainPageLayout + the sidebars register a setter per
 * region ('panes' | 'left' | 'right') so the same action also resizes the live
 * panes/cards. Live sizes are read through refs and snapshotted only when the
 * dialog opens, so dragging never re-renders this provider (which wraps the
 * entire app).
 */
export type LayoutRegion = 'panes' | 'left' | 'right';
type PaneSetter = (sizes: number[]) => void;

interface LayoutPreferenceContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Report the live sizes of a region on every layout change. */
  reportLiveSizes: (region: LayoutRegion, sizes: number[] | null) => void;
  /**
   * Register a region's imperative resize fn on mount and clear it (null) on
   * unmount, so Apply/Reset drive the live panes/cards only while they exist.
   */
  registerPaneSetter: (region: LayoutRegion, setter: PaneSetter | null) => void;
}

const LayoutPreferenceContext = createContext<LayoutPreferenceContextValue>({
  isOpen: false,
  open: () => {},
  close: () => {},
  reportLiveSizes: () => {},
  registerPaneSetter: () => {},
});

export const LayoutPreferenceProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const userProfile = useAppSelector(selectUserProfile);
  const userKey = resolvePaneUserKey(userProfile);
  const isPosCartEnabled = useAppSelector(selectFeatureFlagPosCart);

  const [isOpen, setIsOpen] = useState(false);
  // Values the dialog shows: snapshotted from the live panes/cards / saved
  // preference when it opens (see `open`), never on every drag.
  const [dialogValues, setDialogValues] = useState<{
    panes: number[];
    left: number[];
    right: number[];
  }>({
    panes: DEFAULT_PANE_SIZES,
    left: LEFT_STACK_DEFAULT_SIZES,
    right: RIGHT_STACK_DEFAULT_SIZES,
  });
  // Refs (not state) so per-frame drag reports don't re-render the whole app.
  const liveSizesRef = useRef<Record<LayoutRegion, number[] | null>>({
    panes: null,
    left: null,
    right: null,
  });
  const setterRef = useRef<Record<LayoutRegion, PaneSetter | null>>({
    panes: null,
    left: null,
    right: null,
  });

  const open = useCallback(() => {
    setDialogValues({
      panes:
        liveSizesRef.current.panes ??
        getLayoutPreference(userKey) ??
        DEFAULT_PANE_SIZES,
      left:
        liveSizesRef.current.left ??
        getLeftStackHeight(userKey) ??
        LEFT_STACK_DEFAULT_SIZES,
      right:
        liveSizesRef.current.right ??
        getRightStackHeight(userKey) ??
        RIGHT_STACK_DEFAULT_SIZES,
    });
    setIsOpen(true);
  }, [userKey]);

  const close = useCallback(() => setIsOpen(false), []);

  const reportLiveSizes = useCallback(
    (region: LayoutRegion, sizes: number[] | null) => {
      liveSizesRef.current[region] = sizes;
    },
    []
  );

  const registerPaneSetter = useCallback(
    (region: LayoutRegion, setter: PaneSetter | null) => {
      setterRef.current[region] = setter;
      // Region gone (e.g. agent returned to the lobby): forget its sizes so the
      // dialog falls back to the saved preference / default next time it opens.
      if (!setter) liveSizesRef.current[region] = null;
    },
    []
  );

  const handleApply = useCallback(
    (values: LayoutPreferenceValues) => {
      saveLayoutPreference(userKey, values.panes);
      saveLeftStackHeight(userKey, values.leftStack);
      saveRightStackHeight(userKey, values.rightStack);
      setterRef.current.panes?.(values.panes);
      setterRef.current.left?.(values.leftStack);
      setterRef.current.right?.(values.rightStack);
    },
    [userKey]
  );

  const handleReset = useCallback(() => {
    removeLayoutPreference(userKey);
    removeStackHeights(userKey);
    setterRef.current.panes?.(DEFAULT_PANE_SIZES);
    setterRef.current.left?.(LEFT_STACK_DEFAULT_SIZES);
    setterRef.current.right?.(RIGHT_STACK_DEFAULT_SIZES);
  }, [userKey]);

  const value = useMemo(
    () => ({ isOpen, open, close, reportLiveSizes, registerPaneSetter }),
    [isOpen, open, close, reportLiveSizes, registerPaneSetter]
  );

  return (
    <LayoutPreferenceContext.Provider value={value}>
      {children}
      <LayoutPreferenceDialog
        open={isOpen}
        onClose={close}
        paneSizes={dialogValues.panes}
        leftStackSizes={dialogValues.left}
        rightStackSizes={dialogValues.right}
        isPosCartEnabled={isPosCartEnabled}
        onApply={handleApply}
        onReset={handleReset}
      />
    </LayoutPreferenceContext.Provider>
  );
};

export const useLayoutPreference = () => useContext(LayoutPreferenceContext);
